/**
 * Hooks para buscar e gerenciar dados de documentos
 */
import { useQuery } from '@tanstack/react-query';
import { useDocumentosFilters } from './useDocumentosFilters';
import { API_BASE_URL } from '@/lib/api';

const API_BASE = `${API_BASE_URL}/claro/api/documents`;

// Tipos para respostas da API

/** KPIs de documentos */
export interface DocumentosKpisResponse {
  total: number;
  vigentes: number;
  permanentes: number;
  emAndamento: number;
  irregulares: number;
}

/** Status count para gráficos */
export interface StatusCount {
  status: string;
  count: number;
}

/** Tipos de documentos disponíveis */
export interface DocumentTypesResponse {
  types: string[];
  defaults: string[];
}

/** Documentos por situação */
export interface UnitsBySituationResponse {
  regular: { count: number };
  renovacao: { count: number };
  irregular: { count: number };
  total: number;
  totalUnits: number;
}

/** Linha da tabela de unidades */
export interface UnitsTableRow {
  unitId: string;
  identification: string;
  cnpj: string;
  regional: string;
  avcbStatus: string;
  alvaraStatus: string;
}

/** Resposta da tabela de unidades */
export interface UnitsTableResponse {
  data: UnitsTableRow[];
  total: number;
}

/**
 * Helper para fazer fetch da API
 */
async function fetchJson<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);
  
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
  }
  
  const response = await fetch(url.toString());
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  
  const json = await response.json();
  return json.result ?? json;
}

/**
 * Hook para buscar KPIs de documentos
 */
export function useDocumentosKpis() {
  const activeDocTypes = useDocumentosFilters((state) => state.activeDocTypes);
  const regional = useDocumentosFilters((state) => state.regional);
  const uf = useDocumentosFilters((state) => state.uf);
  
  const params: Record<string, string> = {};
  if (regional) params.regional = regional;
  if (uf) params.uf = uf;
  if (activeDocTypes.length > 0) params.docTypes = activeDocTypes.join(',');

  return useQuery({
    queryKey: ['documents', 'kpis', params],
    queryFn: () => fetchJson<DocumentosKpisResponse>('/kpis', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook para buscar tipos de documentos disponíveis
 */
export function useDocumentTypes() {
  return useQuery({
    queryKey: ['documents', 'types'],
    queryFn: () => fetchJson<DocumentTypesResponse>('/types'),
    staleTime: 1000 * 60 * 30, // 30 minutos
    gcTime: 1000 * 60 * 60, // 1 hora
  });
}

/**
 * Hook para buscar documentos agrupados por status
 */
export function useDocumentosByStatus() {
  const activeDocTypes = useDocumentosFilters((state) => state.activeDocTypes);
  const regional = useDocumentosFilters((state) => state.regional);
  const uf = useDocumentosFilters((state) => state.uf);
  
  const params: Record<string, string> = {};
  if (regional) params.regional = regional;
  if (uf) params.uf = uf;
  if (activeDocTypes.length > 0) params.docTypes = activeDocTypes.join(',');

  return useQuery({
    queryKey: ['documents', 'by-type-status', params],
    queryFn: () => fetchJson<StatusCount[]>('/by-type-status', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook para buscar unidades por situação de documentos
 */
export function useUnitsBySituation() {
  const activeDocTypes = useDocumentosFilters((state) => state.activeDocTypes);
  const regional = useDocumentosFilters((state) => state.regional);
  const uf = useDocumentosFilters((state) => state.uf);
  
  const params: Record<string, string> = {};
  if (regional) params.regional = regional;
  if (uf) params.uf = uf;
  if (activeDocTypes.length > 0) params.docTypes = activeDocTypes.join(',');

  return useQuery({
    queryKey: ['documents', 'units-by-situation', params],
    queryFn: () => fetchJson<UnitsBySituationResponse>('/units-by-situation', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook para buscar tabela de unidades com status AVCB/Alvará
 */
export function useUnitsTable() {
  const regional = useDocumentosFilters((state) => state.regional);
  const uf = useDocumentosFilters((state) => state.uf);
  
  const params: Record<string, string> = {};
  if (regional) params.regional = regional;
  if (uf) params.uf = uf;

  return useQuery({
    queryKey: ['documents', 'units-table', params],
    queryFn: () => fetchJson<UnitsTableResponse>('/units-table', params),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}

/**
 * Hook para buscar lista de regionais disponíveis
 */
export function useRegionals() {
  return useQuery({
    queryKey: ['units', 'regionals'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/claro/api/units/regionals`);
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      const json = await response.json();
      return json.result as string[];
    },
    staleTime: 1000 * 60 * 30, // 30 minutos
    gcTime: 1000 * 60 * 60, // 1 hora
  });
}
